/**
 * Can this page record a voice, and where is it running?
 *
 * Every paid visitor arrives inside Facebook's or Instagram's own browser
 * (2026-10-14), not Safari or Chrome, and the warm-up asks them to talk into
 * the phone on the first screen after the intro. Those browsers are the host
 * app's web view, so the microphone is the host app's to give: on iOS the page
 * gets one only if the app has a microphone permission of its own and answers
 * WebKit's media-capture question, on Android only if the app forwards the
 * web view's permission request. What a page sees when it does not is any of
 * `mediaDevices` missing altogether, a refusal nobody was asked, or a promise
 * that never settles — the third is the dangerous one, because it leaves the
 * screen saying "your phone is asking" over a phone that is asking nothing.
 *
 * On 2026-10-15 a visitor in Facebook's Android browser tapped Record and stayed
 * on that screen for 43 seconds with no answer recorded either way, then left
 * (PostHog session 01a13d63). So the warm-up asks here first — what the
 * browser is, whether recording is possible at all — and gives the
 * microphone a deadline, and every way it can go is counted by browser
 * (`warmup_mic`, `warmup_mic_check`).
 *
 * Pure apart from `askForMic`, so the detection is unit-tested in mic.test.ts.
 */

export type InApp = 'facebook' | 'instagram' | null;
export type Platform = 'ios' | 'android' | 'other';

/**
 * Facebook's and Instagram's browsers say who they are in the user agent:
 * `[FBAN/FBIOS;FBAV/…]` on iOS, `[FB_IAB/FB4A;FBAV/…]` on Android (both seen in
 * PostHog, October 2026), and `Instagram 3xx.0…` in both of Instagram's.
 * Messenger's is Facebook's (`FBAN/MessengerForiOS`, `FB_IAB/MESSENGER`), and
 * behaves like it. Instagram is checked first: some of its builds carry FB
 * tokens as well.
 */
export function inAppBrowser(ua: string): InApp {
  if (/\bInstagram\b/i.test(ua)) return 'instagram';
  if (/FBAN\/|FBAV\/|FB_IAB\/|FBIOS|FB4A/.test(ua)) return 'facebook';
  return null;
}

/** iPadOS asks for the desktop site and says Macintosh; a touch screen gives it away. */
export function platformOf(ua: string, touchPoints = 0): Platform {
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/**
 * Whether there is anything to ask: a secure page, `getUserMedia`, and a
 * `MediaRecorder` to put the voice in. A no here means the Record button can
 * only ever disappoint, so the warm-up offers typing instead from the start.
 */
export function canRecord(env: { secure?: boolean; getUserMedia?: unknown; recorder?: unknown }): boolean {
  return env.secure !== false && typeof env.getUserMedia === 'function' && typeof env.recorder === 'function';
}

export interface MicPlace {
  /** Facebook, Instagram, or a real browser. */
  browser: 'facebook' | 'instagram' | 'browser';
  os: Platform;
  /** There is a microphone API and a recorder; nothing has been asked yet. */
  recordable: boolean;
}

export function micPlace(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): MicPlace {
  const ua = nav?.userAgent || '';
  const where = inAppBrowser(ua);
  return {
    browser: where ?? 'browser',
    os: platformOf(ua, nav?.maxTouchPoints || 0),
    recordable: canRecord({
      secure: typeof window === 'undefined' ? true : window.isSecureContext,
      getUserMedia: nav?.mediaDevices?.getUserMedia,
      recorder: typeof MediaRecorder === 'undefined' ? undefined : MediaRecorder,
    }),
  };
}

/**
 * What the browser already says about the microphone, without asking. Counted,
 * not obeyed: a web view can say `denied` for a microphone its host app would
 * still ask for, and headless Chromium says `denied` to everything. Safari 16+
 * and Chromium answer; anything that cannot is `unknown`.
 */
export async function micPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    const s = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
    return s ? s.state : 'unknown';
  } catch {
    return 'unknown';
  }
}

export type MicOutcome = 'granted' | 'denied' | 'unavailable' | 'failed' | 'no-answer';

/**
 * The names a refusal comes back with, sorted into what they mean for the
 * person. `NotAllowedError` is a no (theirs, or the host app's on their behalf);
 * `NotFoundError`, `OverconstrainedError` and a `TypeError` from a missing API
 * mean there is no microphone to be had; `NotReadableError` and `AbortError`
 * mean the hardware is busy or broke — a phone call, another app.
 */
export function outcomeOf(error: unknown): MicOutcome {
  const name = (error as { name?: string } | null)?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError' || name === 'TypeError') return 'unavailable';
  return 'failed';
}

/**
 * How long a microphone question can stay unanswered before the screen offers
 * a way on without it. A person reading a real prompt answers in a few
 * seconds; a browser that never shows one never answers at all. The question
 * is never withdrawn on a timer — a yes at second twenty is still a yes — but
 * at `MIC_NO_ANSWER_MS` the wait is counted once as `no-answer`, so a browser
 * that hangs shows up in PostHog even when the person just closes the tab.
 */
export const MIC_ESCAPE_MS = 4000;
export const MIC_NO_ANSWER_MS = 15000;

/**
 * The one way to open this same page in the phone's own browser, from inside
 * Facebook's or Instagram's, or null where there is none worth offering.
 *
 * iOS: `x-safari-https://`, a Safari URL scheme the host web view may hand to
 * the system. Fragile (research 2026-10-14): Instagram has blocked it since
 * about version 417, and on Facebook it is reported to work only through
 * `window.open`, which is how the link opens it. Android: an `intent://` url
 * naming Chrome, with the page itself as the fallback, reported to work from
 * Facebook, Instagram and Messenger (inapp-debugger, 2024-11). None of this
 * is documented by Meta and any of it can stop working in the next app
 * version, which is why it is a link the person chooses, never a redirect
 * (a redirect by user agent is what Meta's cloaking rule is about), and why
 * `warmup_open_browser` is counted. The new browser has none of this one's
 * storage, so the url carries the campaign and lands on the warm-up itself
 * rather than the intro they have already seen.
 */
export function openInBrowserUrl(place: Pick<MicPlace, 'browser' | 'os'>, href: string): string | null {
  if (place.browser === 'browser') return null;
  const url = new URL(href);
  if (url.protocol !== 'https:') return null;
  if (place.os === 'ios') return `x-safari-https://${url.host}${url.pathname}${url.search}`;
  if (place.os === 'android') return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url.toString())};end`;
  return null;
}

/**
 * Ask for the microphone, and let the person stop waiting.
 *
 * Settles with the outcome, or as `no-answer` when `signal` aborts (they chose
 * to go on without it). A stream that turns up after that is closed at once,
 * so a late yes never leaves the microphone live behind a screen that has
 * moved on; `onLate` hears about the late answer, for the count, and nothing
 * else.
 */
export function askForMic(opts: { onLate?: (o: MicOutcome) => void; signal?: AbortSignal } = {}): Promise<{ outcome: MicOutcome; stream?: MediaStream; error?: string }> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (r: { outcome: MicOutcome; stream?: MediaStream; error?: string }) => {
      if (settled) return false;
      settled = true;
      resolve(r);
      return true;
    };
    opts.signal?.addEventListener('abort', () => settle({ outcome: 'no-answer' }));
    let asked: Promise<MediaStream>;
    try {
      // a missing `mediaDevices` throws a TypeError here, which is "unavailable"
      asked = navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      settle({ outcome: outcomeOf(e), error: (e as Error)?.name || String(e) });
      return;
    }
    asked.then(
      (stream) => {
        if (!settle({ outcome: 'granted', stream })) {
          stream.getTracks().forEach((t) => t.stop());
          opts.onLate?.('granted');
        }
      },
      (e) => {
        if (!settle({ outcome: outcomeOf(e), error: (e as Error)?.name || String(e) })) opts.onLate?.(outcomeOf(e));
      },
    );
  });
}
